import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
import { financeReconciliationAdapterEvidenceCodeSchema, financeReconciliationAdapterMaximumSourceBytes, } from '../contracts/finance-reconciliation-adapter.contract.js';
import { financeExpenseReceiptContentTypeSchema } from '../contracts/finance-expense.contract.js';
import { financeProjectProfitabilityUnavailableReasonSchema, } from '../contracts/finance-project-profitability.contract.js';
export const financeExpenseConfigurationValueSchema = z.strictObject({
    currencyCode: z.literal('USD'),
    defaultScope: z.enum(['job', 'shop', 'fleet', 'overhead', 'reimbursement', 'other']),
    defaultCategory: z.enum([
        'materials', 'equipment', 'fuel', 'subcontractor', 'meals', 'office', 'travel', 'other',
    ]),
    defaultPaymentMethod: z.enum(['company-card', 'personal-card', 'cash', 'check', 'other']),
    maximumReceiptBytes: z.number().int().min(1_024).max(100 * 1_024 * 1_024),
    maximumReceiptObjects: z.number().int().min(1).max(20),
    allowedReceiptContentTypes: z.array(financeExpenseReceiptContentTypeSchema).min(1).max(30),
    uploadAuthorizationSeconds: z.number().int().min(60).max(3_600),
    receiptAnalysisEnabled: z.boolean(),
    analysisMaximumAttempts: z.number().int().min(1).max(20),
    analysisBatchSize: z.number().int().min(1).max(100),
    lowConfidenceBasisPoints: z.number().int().min(0).max(10_000),
});
const financeExpenseConfigurationVersionTwoAdditions = {
    allowedReceiptContentTypes: [
        'image/heic', 'image/heif', 'image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/tiff',
    ],
    uploadAuthorizationSeconds: 900,
};
export const financeExpenseConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'finance.expenses.configuration',
        moduleId: 'finance',
        title: 'Expense configuration',
        helpText: 'Controls protected tenant expense defaults and provider-neutral receipt-analysis limits.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            currencyCode: 'USD',
            defaultScope: 'shop',
            defaultCategory: 'other',
            defaultPaymentMethod: 'company-card',
            maximumReceiptBytes: 25 * 1_024 * 1_024,
            maximumReceiptObjects: 20,
            ...financeExpenseConfigurationVersionTwoAdditions,
            receiptAnalysisEnabled: true,
            analysisMaximumAttempts: 5,
            analysisBatchSize: 10,
            lowConfidenceBasisPoints: 7_000,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: financeExpenseConfigurationValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => ({
                ...financeExpenseConfigurationVersionTwoAdditions,
                ...(typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}),
            }),
        }],
});
export const financeExpenseDisplayValueSchema = z.strictObject({
    cardSize: z.enum(['compact', 'standard', 'wide']),
    density: z.enum(['comfortable', 'compact']),
    defaultStatus: z.enum(['all', 'pending', 'flagged', 'approved', 'rejected', 'void']),
    defaultScope: z.enum(['all', 'job', 'shop', 'fleet', 'overhead', 'reimbursement', 'other']),
    showSummary: z.boolean(),
    showReceiptAnalysis: z.boolean(),
    showReceiptReferences: z.boolean(),
    listPageSize: z.number().int().min(10).max(250),
});
export const financeExpenseDisplaySetting = definePlatformSetting({
    definition: {
        key: 'finance.expenses.display', moduleId: 'finance', title: 'Expense display',
        helpText: 'Controls tenant and user expense card sizing, density, filters, and receipt details.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            cardSize: 'standard', density: 'comfortable', defaultStatus: 'all', defaultScope: 'all',
            showSummary: true, showReceiptAnalysis: true, showReceiptReferences: true, listPageSize: 50,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: true, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: financeExpenseDisplayValueSchema,
});
const expenseLabel = z.string().trim().min(1).max(120);
export const financeExpenseCopyValueSchema = z.strictObject({
    navigationLabel: expenseLabel,
    title: expenseLabel,
    description: z.string().trim().min(1).max(300),
    createTitle: expenseLabel,
    detailTitle: expenseLabel,
    summaryTitle: expenseLabel,
    vendorLabel: expenseLabel,
    descriptionLabel: expenseLabel,
    scopeLabel: expenseLabel,
    categoryLabel: expenseLabel,
    paymentMethodLabel: expenseLabel,
    cardLastFourLabel: expenseLabel,
    expenseDateLabel: expenseLabel,
    subtotalLabel: expenseLabel,
    taxLabel: expenseLabel,
    totalLabel: expenseLabel,
    projectLabel: expenseLabel,
    receiptObjectsLabel: expenseLabel,
    statusLabel: expenseLabel,
    reasonLabel: expenseLabel,
    createAction: expenseLabel,
    saveAction: expenseLabel,
    approveAction: expenseLabel,
    rejectAction: expenseLabel,
    voidAction: expenseLabel,
    requestAnalysisAction: expenseLabel,
    uploadReceiptTitle: expenseLabel,
    uploadReceiptAction: expenseLabel,
    selectedReceiptLabel: expenseLabel,
    uploadProgressLabel: z.string().trim().min(1).max(200),
    uploadCompleteLabel: z.string().trim().min(1).max(200),
    uploadPolicyErrorLabel: z.string().trim().min(1).max(240),
    bulkApproveAction: expenseLabel,
    selectAllPendingAction: expenseLabel,
    bulkReasonLabel: expenseLabel,
    bulkResultLabel: z.string().trim().min(1).max(240),
    openAction: expenseLabel,
    closeAction: expenseLabel,
    loadingLabel: expenseLabel,
    emptyLabel: z.string().trim().min(1).max(240),
    loadErrorLabel: z.string().trim().min(1).max(240),
    mutationErrorLabel: z.string().trim().min(1).max(240),
    amountRestrictedLabel: z.string().trim().min(1).max(240),
    receiptAnalysisTitle: expenseLabel,
    receiptAnalysisDisabledLabel: z.string().trim().min(1).max(240),
    scopes: z.record(z.enum(['job', 'shop', 'fleet', 'overhead', 'reimbursement', 'other']), expenseLabel),
    categories: z.record(z.enum([
        'materials', 'equipment', 'fuel', 'subcontractor', 'meals', 'office', 'travel', 'other',
    ]), expenseLabel),
    paymentMethods: z.record(z.enum(['company-card', 'personal-card', 'cash', 'check', 'other']), expenseLabel),
    statuses: z.record(z.enum(['pending', 'flagged', 'approved', 'rejected', 'void']), expenseLabel),
});
const financeExpenseCopyVersionTwoAdditions = {
    uploadReceiptTitle: 'Scan a receipt',
    uploadReceiptAction: 'Upload and scan receipt',
    selectedReceiptLabel: 'Receipt image',
    uploadProgressLabel: 'Preparing receipt: {percent}%',
    uploadCompleteLabel: 'Receipt uploaded and expense created.',
    uploadPolicyErrorLabel: 'Choose an allowed receipt image within the tenant file-size limit.',
    bulkApproveAction: 'Approve selected',
    selectAllPendingAction: 'Select pending and flagged',
    bulkReasonLabel: 'Bulk approval reason',
    bulkResultLabel: 'Approved {approved}; skipped {skipped}.',
};
export const financeExpenseCopySetting = definePlatformSetting({
    definition: {
        key: 'finance.copy.expenses', moduleId: 'finance', title: 'Expense interface copy',
        helpText: 'Controls tenant-editable expense, approval, and receipt-analysis labels.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 2,
        codedFallback: {
            navigationLabel: 'Expenses', title: 'Expenses',
            description: 'Record, review, and approve exact-cent tenant expenses.',
            createTitle: 'Record expense', detailTitle: 'Expense review', summaryTitle: 'Expense summary',
            vendorLabel: 'Vendor', descriptionLabel: 'Description', scopeLabel: 'Scope',
            categoryLabel: 'Category', paymentMethodLabel: 'Payment method',
            cardLastFourLabel: 'Card last four', expenseDateLabel: 'Expense date',
            subtotalLabel: 'Subtotal', taxLabel: 'Tax', totalLabel: 'Total', projectLabel: 'Project ID',
            receiptObjectsLabel: 'Finalized receipt object IDs', statusLabel: 'Status', reasonLabel: 'Reason',
            createAction: 'Create expense', saveAction: 'Save revision', approveAction: 'Approve',
            rejectAction: 'Reject', voidAction: 'Void', requestAnalysisAction: 'Request receipt analysis',
            ...financeExpenseCopyVersionTwoAdditions,
            openAction: 'Open expense', closeAction: 'Close', loadingLabel: 'Loading expenses…',
            emptyLabel: 'No expenses match these filters.', loadErrorLabel: 'Expenses are unavailable.',
            mutationErrorLabel: 'The expense was not saved. Refresh and try again.',
            amountRestrictedLabel: 'Expense amounts are restricted for this record.',
            receiptAnalysisTitle: 'Receipt analysis',
            receiptAnalysisDisabledLabel: 'Receipt analysis is disabled; manual entry remains available.',
            scopes: { job: 'Job', shop: 'Shop', fleet: 'Fleet', overhead: 'Overhead', reimbursement: 'Reimbursement', other: 'Other' },
            categories: { materials: 'Materials', equipment: 'Equipment', fuel: 'Fuel', subcontractor: 'Subcontractor', meals: 'Meals', office: 'Office', travel: 'Travel', other: 'Other' },
            paymentMethods: { 'company-card': 'Company card', 'personal-card': 'Personal card', cash: 'Cash', check: 'Check', other: 'Other' },
            statuses: { pending: 'Pending', flagged: 'Flagged', approved: 'Approved', rejected: 'Rejected', void: 'Void' },
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: financeExpenseCopyValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => ({
                ...financeExpenseCopyVersionTwoAdditions,
                ...(typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}),
            }),
        }],
});
export const financeInvoiceConfigurationValueSchema = z.strictObject({
    numberPrefix: z.string().regex(/^[A-Z][A-Z0-9-]{0,11}$/),
    startingNumber: z.number().int().min(1).max(999_999_999),
    defaultDueDays: z.number().int().min(0).max(365),
    defaultTaxBasisPoints: z.number().int().min(0).max(10_000),
    currencyCode: z.literal('USD'),
    defaultUnitLabel: z.string().trim().min(1).max(20),
    projectPanelEnabled: z.boolean(),
    listPageSize: z.number().int().min(10).max(100),
});
export const financeInvoiceConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'finance.invoices.configuration',
        moduleId: 'finance',
        title: 'Invoice configuration',
        helpText: 'Controls protected tenant invoice numbering, due dates, tax defaults, and presentation.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            numberPrefix: 'INV',
            startingNumber: 1001,
            defaultDueDays: 30,
            defaultTaxBasisPoints: 0,
            currencyCode: 'USD',
            defaultUnitLabel: 'ea',
            projectPanelEnabled: true,
            listPageSize: 30,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'protected',
    },
    valueSchema: financeInvoiceConfigurationValueSchema,
});
export const financeInvoiceCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(240),
    projectPanelTitle: z.string().trim().min(1).max(100),
    projectPanelDescription: z.string().trim().min(1).max(240),
    emptyLabel: z.string().trim().min(1).max(180),
    loadingLabel: z.string().trim().min(1).max(120),
    errorLabel: z.string().trim().min(1).max(240),
    invoiceNumberLabel: z.string().trim().min(1).max(80),
    projectLabel: z.string().trim().min(1).max(80),
    statusLabel: z.string().trim().min(1).max(80),
    statusDraftLabel: z.string().trim().min(1).max(80),
    statusSentLabel: z.string().trim().min(1).max(80),
    statusPartialLabel: z.string().trim().min(1).max(80),
    statusPaidLabel: z.string().trim().min(1).max(80),
    statusVoidLabel: z.string().trim().min(1).max(80),
    totalLabel: z.string().trim().min(1).max(80),
    amountDueLabel: z.string().trim().min(1).max(80),
    dueDateLabel: z.string().trim().min(1).max(80),
    createDraftTitle: z.string().trim().min(1).max(100),
    lineLabel: z.string().trim().min(1).max(80),
    descriptionLabel: z.string().trim().min(1).max(80),
    quantityLabel: z.string().trim().min(1).max(80),
    unitPriceLabel: z.string().trim().min(1).max(80),
    discountLabel: z.string().trim().min(1).max(80),
    memoLabel: z.string().trim().min(1).max(80),
    addLineAction: z.string().trim().min(1).max(80),
    removeLineAction: z.string().trim().min(1).max(80),
    createDraftAction: z.string().trim().min(1).max(80),
    invalidDraftLabel: z.string().trim().min(1).max(240),
    mutationErrorLabel: z.string().trim().min(1).max(240),
    openInvoicesAction: z.string().trim().min(1).max(100),
});
export const financeInvoiceCopySetting = definePlatformSetting({
    definition: {
        key: 'finance.copy.invoices',
        moduleId: 'finance',
        title: 'Invoice interface copy',
        helpText: 'Controls tenant-editable labels for invoice lists and the Project billing panel.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Invoices',
            title: 'Invoices',
            description: 'Review tenant invoice drafts and their immutable billing-recipient snapshots.',
            projectPanelTitle: 'Project billing',
            projectPanelDescription: 'Create and review exact-cent invoice drafts for this Project.',
            emptyLabel: 'No invoice drafts have been created.',
            loadingLabel: 'Loading invoices…',
            errorLabel: 'Invoice information is unavailable.',
            invoiceNumberLabel: 'Invoice',
            projectLabel: 'Project',
            statusLabel: 'Status',
            statusDraftLabel: 'Draft',
            statusSentLabel: 'Sent',
            statusPartialLabel: 'Partially paid',
            statusPaidLabel: 'Paid',
            statusVoidLabel: 'Void',
            totalLabel: 'Total',
            amountDueLabel: 'Due',
            dueDateLabel: 'Due date',
            createDraftTitle: 'Create draft invoice',
            lineLabel: 'Invoice line',
            descriptionLabel: 'Line description',
            quantityLabel: 'Quantity',
            unitPriceLabel: 'Unit price',
            discountLabel: 'Discount',
            memoLabel: 'Memo',
            addLineAction: 'Add line',
            removeLineAction: 'Remove',
            createDraftAction: 'Create draft',
            invalidDraftLabel: 'Check the line quantities, prices, discount, and required descriptions.',
            mutationErrorLabel: 'The invoice draft was not created. Reload billing before trying again.',
            openInvoicesAction: 'Open all invoices',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: financeInvoiceCopyValueSchema,
});
export const financeInvoiceDisplayValueSchema = z.strictObject({
    cardSize: z.enum(['compact', 'standard', 'wide']),
    density: z.enum(['comfortable', 'compact']),
    defaultFilter: z.enum(['all', 'overdue', 'draft', 'sent', 'partial', 'paid', 'void']),
    showLifecyclePanel: z.boolean(),
    showTimeline: z.boolean(),
    showPaymentReferences: z.boolean(),
    showContentEditor: z.boolean(),
    showContentHistory: z.boolean(),
    showEstimatingImport: z.boolean(),
    contentHistoryLimit: z.number().int().min(1).max(100),
});
const financeInvoiceDisplayVersionTwoAdditions = {
    showContentEditor: true,
    showContentHistory: true,
    showEstimatingImport: true,
    contentHistoryLimit: 20,
};
export const financeInvoiceDisplaySetting = definePlatformSetting({
    definition: {
        key: 'finance.invoices.display',
        moduleId: 'finance',
        title: 'Invoice display',
        helpText: 'Controls invoice card size, density, default filters, lifecycle detail, and payment references.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            cardSize: 'standard',
            density: 'comfortable',
            defaultFilter: 'all',
            showLifecyclePanel: true,
            showTimeline: true,
            showPaymentReferences: true,
            ...financeInvoiceDisplayVersionTwoAdditions,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: financeInvoiceDisplayValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => ({
                ...financeInvoiceDisplayVersionTwoAdditions,
                ...(typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}),
            }),
        }],
});
const lifecycleLabel = z.string().trim().min(1).max(120);
export const financeInvoiceContentCopyValueSchema = z.strictObject({
    estimatingImportTitle: lifecycleLabel,
    estimatingImportDescription: z.string().trim().min(1).max(300),
    estimatingUnavailableLabel: z.string().trim().min(1).max(240),
    estimatingCreateAction: lifecycleLabel,
    estimatingCreatedLabel: z.string().trim().min(1).max(240),
    estimateRevisionLabel: lifecycleLabel,
    estimateStatusLabel: lifecycleLabel,
    estimateLinesLabel: lifecycleLabel,
    estimateStatusLabels: z.strictObject({
        draft: lifecycleLabel,
        released: lifecycleLabel,
        viewed: lifecycleLabel,
        accepted: lifecycleLabel,
        declined: lifecycleLabel,
        expired: lifecycleLabel,
    }),
    contentTitle: lifecycleLabel,
    contentEditorTitle: lifecycleLabel,
    contentHistoryTitle: lifecycleLabel,
    contentRevisionLabel: lifecycleLabel,
    contentHashLabel: lifecycleLabel,
    sourceLabel: lifecycleLabel,
    manualSourceLabel: lifecycleLabel,
    estimatingSourceLabel: lifecycleLabel,
    taxRateLabel: lifecycleLabel,
    taxableLabel: lifecycleLabel,
    unitLabelLabel: lifecycleLabel,
    reasonLabel: lifecycleLabel,
    saveContentAction: lifecycleLabel,
    noHistoryLabel: z.string().trim().min(1).max(240),
    readOnlyLabel: z.string().trim().min(1).max(240),
    loadErrorLabel: z.string().trim().min(1).max(240),
    mutationErrorLabel: z.string().trim().min(1).max(240),
});
export const financeInvoiceContentCopySetting = definePlatformSetting({
    definition: {
        key: 'finance.copy.invoice-content',
        moduleId: 'finance',
        title: 'Invoice content interface copy',
        helpText: 'Controls tenant-editable Estimating intake, invoice-line revision, and content-history labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            estimatingImportTitle: 'Create from estimate',
            estimatingImportDescription: 'Use the current retained Estimating revision without copying Estimating tables into Finance.',
            estimatingUnavailableLabel: 'No billable estimate is available for this Project.',
            estimatingCreateAction: 'Create invoice from estimate',
            estimatingCreatedLabel: 'The invoice draft was created from the retained estimate revision.',
            estimateRevisionLabel: 'Estimate revision',
            estimateStatusLabel: 'Estimate status',
            estimateLinesLabel: 'Billable lines',
            estimateStatusLabels: {
                draft: 'Draft', released: 'Released', viewed: 'Viewed', accepted: 'Accepted',
                declined: 'Declined', expired: 'Expired',
            },
            contentTitle: 'Invoice content',
            contentEditorTitle: 'Revise invoice lines',
            contentHistoryTitle: 'Content history',
            contentRevisionLabel: 'Content revision',
            contentHashLabel: 'Content hash',
            sourceLabel: 'Source',
            manualSourceLabel: 'Manual invoice content',
            estimatingSourceLabel: 'Estimating billable revision',
            taxRateLabel: 'Tax rate',
            taxableLabel: 'Taxable',
            unitLabelLabel: 'Unit',
            reasonLabel: 'Change reason',
            saveContentAction: 'Save content revision',
            noHistoryLabel: 'No retained content history is available.',
            readOnlyLabel: 'Paid and void invoices retain their content and cannot be edited.',
            loadErrorLabel: 'Invoice content history is unavailable.',
            mutationErrorLabel: 'The invoice content was not saved. Refresh and try again.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: financeInvoiceContentCopyValueSchema,
});
const financePaymentMethodLabelsSchema = z.strictObject({
    'credit-card': lifecycleLabel,
    ach: lifecycleLabel,
    check: lifecycleLabel,
    cash: lifecycleLabel,
    avidxchange: lifecycleLabel,
    other: lifecycleLabel,
});
const financeWriteOffReasonLabelsSchema = z.strictObject({
    'wire-fee': lifecycleLabel,
    'customer-negotiation': lifecycleLabel,
    settlement: lifecycleLabel,
    rounding: lifecycleLabel,
    'bad-debt': lifecycleLabel,
    other: lifecycleLabel,
});
const financePaymentReversalKindLabelsSchema = z.strictObject({
    failed: lifecycleLabel,
    refunded: lifecycleLabel,
    voided: lifecycleLabel,
    duplicate: lifecycleLabel,
    other: lifecycleLabel,
});
const financeInvoiceEventTypeLabelsSchema = z.strictObject({
    revised: lifecycleLabel,
    'content-revised': lifecycleLabel,
    issued: lifecycleLabel,
    voided: lifecycleLabel,
    'written-off': lifecycleLabel,
    'payment-pending': lifecycleLabel,
    'payment-posted': lifecycleLabel,
    'payment-completed': lifecycleLabel,
    'payment-reversed': lifecycleLabel,
    'payment-refunded': lifecycleLabel,
});
export const financeInvoiceLifecycleCopyValueSchema = z.strictObject({
    openAction: lifecycleLabel,
    closeAction: lifecycleLabel,
    retryAction: lifecycleLabel,
    allFilterLabel: lifecycleLabel,
    lifecycleTitle: lifecycleLabel,
    overdueLabel: lifecycleLabel,
    revisionLabel: lifecycleLabel,
    issuedAtLabel: lifecycleLabel,
    settledAtLabel: lifecycleLabel,
    reviseAction: lifecycleLabel,
    issueAction: lifecycleLabel,
    voidAction: lifecycleLabel,
    writeOffAction: lifecycleLabel,
    recordPaymentAction: lifecycleLabel,
    completePaymentAction: lifecycleLabel,
    reversePaymentAction: lifecycleLabel,
    dueDateLabel: lifecycleLabel,
    reasonLabel: lifecycleLabel,
    amountLabel: lifecycleLabel,
    methodLabel: lifecycleLabel,
    referenceLabel: lifecycleLabel,
    memoLabel: lifecycleLabel,
    paymentDateLabel: lifecycleLabel,
    paymentStatusLabel: lifecycleLabel,
    pendingPaymentLabel: lifecycleLabel,
    completedPaymentLabel: lifecycleLabel,
    reversedPaymentLabel: lifecycleLabel,
    paymentsTitle: lifecycleLabel,
    refundsTitle: lifecycleLabel,
    writeOffsTitle: lifecycleLabel,
    timelineTitle: lifecycleLabel,
    noPaymentsLabel: z.string().trim().min(1).max(240),
    noRefundsLabel: z.string().trim().min(1).max(240),
    noWriteOffsLabel: z.string().trim().min(1).max(240),
    noEventsLabel: z.string().trim().min(1).max(240),
    loadingLabel: lifecycleLabel,
    loadErrorLabel: z.string().trim().min(1).max(240),
    mutationErrorLabel: z.string().trim().min(1).max(240),
    revisionConflictLabel: z.string().trim().min(1).max(240),
    paymentMethodLabels: financePaymentMethodLabelsSchema,
    writeOffReasonLabels: financeWriteOffReasonLabelsSchema,
    reversalKindLabels: financePaymentReversalKindLabelsSchema,
    eventTypeLabels: financeInvoiceEventTypeLabelsSchema,
});
export const financeInvoiceLifecycleCopySetting = definePlatformSetting({
    definition: {
        key: 'finance.copy.invoice-lifecycle',
        moduleId: 'finance',
        title: 'Invoice lifecycle interface copy',
        helpText: 'Controls tenant-editable invoice lifecycle, payment, write-off, and timeline labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            openAction: 'Open invoice',
            closeAction: 'Close invoice',
            retryAction: 'Try again',
            allFilterLabel: 'All',
            lifecycleTitle: 'Invoice activity',
            overdueLabel: 'Overdue',
            revisionLabel: 'Revision',
            issuedAtLabel: 'Issued',
            settledAtLabel: 'Settled',
            reviseAction: 'Save invoice details',
            issueAction: 'Issue invoice',
            voidAction: 'Void invoice',
            writeOffAction: 'Record write-off',
            recordPaymentAction: 'Record payment',
            completePaymentAction: 'Complete payment',
            reversePaymentAction: 'Reverse payment',
            dueDateLabel: 'Due date',
            reasonLabel: 'Reason',
            amountLabel: 'Amount',
            methodLabel: 'Method',
            referenceLabel: 'Reference',
            memoLabel: 'Memo',
            paymentDateLabel: 'Payment date',
            paymentStatusLabel: 'Payment state',
            pendingPaymentLabel: 'Pending',
            completedPaymentLabel: 'Completed',
            reversedPaymentLabel: 'Reversed',
            paymentsTitle: 'Payments',
            refundsTitle: 'Refunds',
            writeOffsTitle: 'Write-offs',
            timelineTitle: 'Timeline',
            noPaymentsLabel: 'No payments are recorded for this invoice.',
            noRefundsLabel: 'No refunds are recorded for this invoice.',
            noWriteOffsLabel: 'No write-offs are recorded for this invoice.',
            noEventsLabel: 'No lifecycle events are recorded for this invoice.',
            loadingLabel: 'Loading invoice activity…',
            loadErrorLabel: 'Invoice activity is unavailable.',
            mutationErrorLabel: 'The invoice change was not saved. Reload the invoice and try again.',
            revisionConflictLabel: 'This invoice changed. Reload it before applying another change.',
            paymentMethodLabels: {
                'credit-card': 'Credit card', ach: 'ACH', check: 'Check', cash: 'Cash',
                avidxchange: 'AvidXchange', other: 'Other',
            },
            writeOffReasonLabels: {
                'wire-fee': 'Wire fee', 'customer-negotiation': 'Customer negotiation',
                settlement: 'Settlement', rounding: 'Rounding', 'bad-debt': 'Bad debt', other: 'Other',
            },
            reversalKindLabels: {
                failed: 'Failed', refunded: 'Refunded', voided: 'Voided',
                duplicate: 'Duplicate', other: 'Other',
            },
            eventTypeLabels: {
                revised: 'Invoice revised', 'content-revised': 'Invoice content revised',
                issued: 'Invoice issued', voided: 'Invoice voided',
                'written-off': 'Write-off recorded', 'payment-pending': 'Pending payment recorded',
                'payment-posted': 'Completed payment recorded', 'payment-completed': 'Pending payment completed',
                'payment-reversed': 'Payment reversed', 'payment-refunded': 'Payment refund recorded',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: financeInvoiceLifecycleCopyValueSchema,
});
export const financeOverheadDisplayValueSchema = z.strictObject({
    cardSize: z.enum(['compact', 'standard', 'wide']),
    density: z.enum(['comfortable', 'compact']),
    defaultTab: z.enum(['dashboard', 'bills', 'forecast', 'allocations', 'accounts']),
    showExpenseSummary: z.boolean(),
    showUpcomingBills: z.boolean(),
    dashboardWindowDays: z.number().int().min(7).max(366),
    forecastWindowDays: z.number().int().min(7).max(366),
    listPageSize: z.number().int().min(10).max(250),
});
export const financeOverheadDisplaySetting = definePlatformSetting({
    definition: {
        key: 'finance.overhead.display',
        moduleId: 'finance',
        title: 'Overhead display',
        helpText: 'Controls tenant and user overhead card sizing, density, default view, windows, and page size.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            cardSize: 'standard',
            density: 'comfortable',
            defaultTab: 'dashboard',
            showExpenseSummary: true,
            showUpcomingBills: true,
            dashboardWindowDays: 30,
            forecastWindowDays: 90,
            listPageSize: 100,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: financeOverheadDisplayValueSchema,
});
const overheadLabel = z.string().trim().min(1).max(140);
export const financeOverheadCopyValueSchema = z.strictObject({
    navigationLabel: overheadLabel,
    title: overheadLabel,
    description: z.string().trim().min(1).max(320),
    dashboardTab: overheadLabel,
    billsTab: overheadLabel,
    forecastTab: overheadLabel,
    allocationsTab: overheadLabel,
    accountsTab: overheadLabel,
    approvedExpensesLabel: overheadLabel,
    pendingExpensesLabel: overheadLabel,
    expectedBillsLabel: overheadLabel,
    actualBillsLabel: overheadLabel,
    overdueBillsLabel: overheadLabel,
    upcomingBillsLabel: overheadLabel,
    recurringBillsTitle: overheadLabel,
    occurrencesTitle: overheadLabel,
    forecastTitle: overheadLabel,
    allocationPolicyTitle: overheadLabel,
    allocationPreviewTitle: overheadLabel,
    allocationRunsTitle: overheadLabel,
    costCentersTitle: overheadLabel,
    accountCategoriesTitle: overheadLabel,
    codeLabel: overheadLabel,
    vendorLabel: overheadLabel,
    nameLabel: overheadLabel,
    descriptionLabel: overheadLabel,
    typeLabel: overheadLabel,
    categoryLabel: overheadLabel,
    amountLabel: overheadLabel,
    amountModeLabel: overheadLabel,
    cadenceLabel: overheadLabel,
    dueDateLabel: overheadLabel,
    dueDayLabel: overheadLabel,
    statusLabel: overheadLabel,
    notesLabel: overheadLabel,
    reasonLabel: overheadLabel,
    activeLabel: overheadLabel,
    allocationBasisLabel: overheadLabel,
    paymentMethodLabel: overheadLabel,
    autopayLabel: overheadLabel,
    allocatableLabel: overheadLabel,
    allocationEligibleLabel: overheadLabel,
    enabledLabel: overheadLabel,
    includeDraftInvoicesLabel: overheadLabel,
    defaultRunStatusLabel: overheadLabel,
    paidAtLabel: overheadLabel,
    expenseIdLabel: overheadLabel,
    postingBehaviorLabel: overheadLabel,
    projectLabel: overheadLabel,
    periodStartLabel: overheadLabel,
    periodEndLabel: overheadLabel,
    rateLabel: overheadLabel,
    revenueBasisLabel: overheadLabel,
    annualOverheadLabel: overheadLabel,
    createBillAction: overheadLabel,
    createOccurrenceAction: overheadLabel,
    createCostCenterAction: overheadLabel,
    createAccountCategoryAction: overheadLabel,
    savePolicyAction: overheadLabel,
    previewAllocationAction: overheadLabel,
    createDraftRunAction: overheadLabel,
    lockRunAction: overheadLabel,
    saveAction: overheadLabel,
    editAction: overheadLabel,
    cancelAction: overheadLabel,
    loadingLabel: overheadLabel,
    emptyLabel: z.string().trim().min(1).max(240),
    loadErrorLabel: z.string().trim().min(1).max(240),
    mutationErrorLabel: z.string().trim().min(1).max(240),
    missingPolicyLabel: z.string().trim().min(1).max(240),
    occurrenceStatusLabels: z.record(z.enum(['scheduled', 'uploaded', 'approved', 'paid', 'late', 'skipped', 'cancelled']), overheadLabel),
    billCategoryLabels: z.record(z.enum([
        'rent', 'utilities', 'internet', 'insurance', 'software', 'loan', 'vehicle', 'tax',
        'payroll-related', 'other',
    ]), overheadLabel),
    amountModeLabels: z.record(z.enum(['fixed', 'variable', 'estimate']), overheadLabel),
    cadenceLabels: z.record(z.enum(['weekly', 'biweekly', 'monthly', 'quarterly', 'annual', 'custom']), overheadLabel),
    allocationBasisLabels: z.record(z.enum(['revenue', 'labor-hours', 'project-days', 'active-project-count', 'manual', 'none']), overheadLabel),
    costCenterTypeLabels: z.record(z.enum([
        'project', 'shop', 'fleet', 'office', 'warehouse', 'administration', 'sales', 'owner',
        'overhead', 'other',
    ]), overheadLabel),
    accountTypeLabels: z.record(z.enum([
        'income', 'project-expense', 'overhead', 'payroll', 'commission', 'asset', 'liability',
        'equity', 'other',
    ]), overheadLabel),
    postingBehaviorLabels: z.record(z.enum(['posting', 'reporting-only']), overheadLabel),
    allocationRunStatusLabels: z.record(z.enum(['draft', 'locked', 'superseded']), overheadLabel),
});
export const financeOverheadCopySetting = definePlatformSetting({
    definition: {
        key: 'finance.copy.overhead',
        moduleId: 'finance',
        title: 'Overhead interface copy',
        helpText: 'Controls tenant-editable overhead, recurring-bill, forecast, account, and allocation labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Overhead',
            title: 'Overhead and bills',
            description: 'Track recurring obligations, cost centers, forecasts, and exact-cent Project allocations.',
            dashboardTab: 'Dashboard', billsTab: 'Bills', forecastTab: 'Forecast',
            allocationsTab: 'Allocations', accountsTab: 'Accounts and cost centers',
            approvedExpensesLabel: 'Approved expenses', pendingExpensesLabel: 'Pending expenses',
            expectedBillsLabel: 'Expected bills', actualBillsLabel: 'Actual bills',
            overdueBillsLabel: 'Overdue bills', upcomingBillsLabel: 'Upcoming bills',
            recurringBillsTitle: 'Recurring bills', occurrencesTitle: 'Bill occurrences',
            forecastTitle: 'Cash requirement forecast', allocationPolicyTitle: 'Allocation policy',
            allocationPreviewTitle: 'Project allocation preview', allocationRunsTitle: 'Allocation runs',
            costCentersTitle: 'Cost centers', accountCategoriesTitle: 'Account categories',
            codeLabel: 'Code', vendorLabel: 'Vendor', nameLabel: 'Name',
            descriptionLabel: 'Description', typeLabel: 'Type', categoryLabel: 'Category',
            amountLabel: 'Amount', amountModeLabel: 'Amount mode', cadenceLabel: 'Cadence',
            dueDateLabel: 'Due date', dueDayLabel: 'Due day', statusLabel: 'Status', notesLabel: 'Notes',
            reasonLabel: 'Reason', activeLabel: 'Active', allocationBasisLabel: 'Allocation basis',
            paymentMethodLabel: 'Payment method', autopayLabel: 'Autopay',
            allocatableLabel: 'Allocatable', allocationEligibleLabel: 'Allocation eligible',
            enabledLabel: 'Enabled',
            includeDraftInvoicesLabel: 'Include draft invoices',
            defaultRunStatusLabel: 'Default run status',
            paidAtLabel: 'Paid at', expenseIdLabel: 'Expense ID',
            postingBehaviorLabel: 'Posting behavior', projectLabel: 'Project',
            periodStartLabel: 'Period start', periodEndLabel: 'Period end',
            rateLabel: 'Overhead rate', revenueBasisLabel: 'Annual revenue basis',
            annualOverheadLabel: 'Annual overhead', createBillAction: 'Create recurring bill',
            createOccurrenceAction: 'Create bill occurrence',
            createCostCenterAction: 'Create cost center',
            createAccountCategoryAction: 'Create account category',
            savePolicyAction: 'Save allocation policy',
            previewAllocationAction: 'Preview allocation', createDraftRunAction: 'Create draft run',
            lockRunAction: 'Lock allocation run', saveAction: 'Save', editAction: 'Edit',
            cancelAction: 'Cancel', loadingLabel: 'Loading overhead…',
            emptyLabel: 'No overhead records match this view.',
            loadErrorLabel: 'Overhead information is unavailable.',
            mutationErrorLabel: 'The overhead change was not saved. Refresh and try again.',
            missingPolicyLabel: 'Create an allocation policy before previewing or locking allocations.',
            occurrenceStatusLabels: {
                scheduled: 'Scheduled', uploaded: 'Uploaded', approved: 'Approved', paid: 'Paid',
                late: 'Late', skipped: 'Skipped', cancelled: 'Cancelled',
            },
            billCategoryLabels: {
                rent: 'Rent', utilities: 'Utilities', internet: 'Internet', insurance: 'Insurance',
                software: 'Software', loan: 'Loan', vehicle: 'Vehicle', tax: 'Tax',
                'payroll-related': 'Payroll related', other: 'Other',
            },
            amountModeLabels: { fixed: 'Fixed', variable: 'Variable', estimate: 'Estimate' },
            cadenceLabels: {
                weekly: 'Weekly', biweekly: 'Every two weeks', monthly: 'Monthly',
                quarterly: 'Quarterly', annual: 'Annual', custom: 'Custom',
            },
            allocationBasisLabels: {
                revenue: 'Revenue', 'labor-hours': 'Labor hours', 'project-days': 'Project days',
                'active-project-count': 'Active Project count', manual: 'Manual', none: 'None',
            },
            costCenterTypeLabels: {
                project: 'Project', shop: 'Shop', fleet: 'Fleet', office: 'Office',
                warehouse: 'Warehouse', administration: 'Administration', sales: 'Sales',
                owner: 'Owner', overhead: 'Overhead', other: 'Other',
            },
            accountTypeLabels: {
                income: 'Income', 'project-expense': 'Project expense', overhead: 'Overhead',
                payroll: 'Payroll', commission: 'Commission', asset: 'Asset', liability: 'Liability',
                equity: 'Equity', other: 'Other',
            },
            postingBehaviorLabels: { posting: 'Posting', 'reporting-only': 'Reporting only' },
            allocationRunStatusLabels: {
                draft: 'Draft', locked: 'Locked', superseded: 'Superseded',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: financeOverheadCopyValueSchema,
});
export const financeReconciliationConfigurationValueSchema = z.strictObject({
    currencyCode: z.literal('USD'),
    maximumRowsPerStatement: z.number().int().min(1).max(5_000),
    maximumAdapterSourceBytes: z.number().int().min(1_024)
        .max(financeReconciliationAdapterMaximumSourceBytes),
    maximumAdapterRejects: z.number().int().min(1).max(500),
    automaticExactMatchEnabled: z.boolean(),
    automaticMatchMinimumBasisPoints: z.number().int().min(6_000).max(10_000),
    candidateDateToleranceDays: z.number().int().min(0).max(31),
    allowAmountOnlyCandidates: z.boolean(),
    createExpenseFromTransactionEnabled: z.boolean(),
    receiptRequestIntentEnabled: z.boolean(),
});
const financeReconciliationConfigurationVersionTwoAdditions = {
    maximumAdapterSourceBytes: financeReconciliationAdapterMaximumSourceBytes,
    maximumAdapterRejects: 100,
    createExpenseFromTransactionEnabled: true,
    receiptRequestIntentEnabled: true,
};
export const financeReconciliationConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'finance.reconciliation.configuration',
        moduleId: 'finance',
        title: 'Reconciliation configuration',
        helpText: 'Controls protected normalized statement limits and automatic matching thresholds.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            currencyCode: 'USD',
            maximumRowsPerStatement: 2_000,
            ...financeReconciliationConfigurationVersionTwoAdditions,
            automaticExactMatchEnabled: true,
            automaticMatchMinimumBasisPoints: 10_000,
            candidateDateToleranceDays: 31,
            allowAmountOnlyCandidates: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'protected',
    },
    valueSchema: financeReconciliationConfigurationValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => ({
                ...financeReconciliationConfigurationVersionTwoAdditions,
                ...(typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}),
            }),
        }],
});
export const financeReconciliationDisplayValueSchema = z.strictObject({
    cardSize: z.enum(['compact', 'standard', 'wide']),
    density: z.enum(['comfortable', 'compact']),
    defaultStatus: z.enum(['all', 'ready', 'in-review', 'reconciled', 'closed', 'failed']),
    showDashboard: z.boolean(),
    showCandidates: z.boolean(),
    showAccountLastFour: z.boolean(),
    showSourceAdapters: z.boolean(),
    showReceiptRequestHistory: z.boolean(),
    statementPageSize: z.number().int().min(10).max(250),
});
const financeReconciliationDisplayVersionTwoAdditions = {
    showSourceAdapters: true,
    showReceiptRequestHistory: true,
};
export const financeReconciliationDisplaySetting = definePlatformSetting({
    definition: {
        key: 'finance.reconciliation.display',
        moduleId: 'finance',
        title: 'Reconciliation display',
        helpText: 'Controls tenant and user statement layout, density, filters, and candidate visibility.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            cardSize: 'standard', density: 'comfortable', defaultStatus: 'all',
            showDashboard: true, showCandidates: true, showAccountLastFour: true,
            ...financeReconciliationDisplayVersionTwoAdditions,
            statementPageSize: 50,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: financeReconciliationDisplayValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => ({
                ...financeReconciliationDisplayVersionTwoAdditions,
                ...(typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}),
            }),
        }],
});
const reconciliationLabel = z.string().trim().min(1).max(160);
export const financeReconciliationCopyValueSchema = z.strictObject({
    navigationLabel: reconciliationLabel,
    title: reconciliationLabel,
    description: z.string().trim().min(1).max(360),
    dashboardTitle: reconciliationLabel,
    statementCountLabel: reconciliationLabel,
    transactionCountLabel: reconciliationLabel,
    matchedCountLabel: reconciliationLabel,
    unresolvedCountLabel: reconciliationLabel,
    noReceiptCountLabel: reconciliationLabel,
    ignoredCountLabel: reconciliationLabel,
    debitTotalLabel: reconciliationLabel,
    creditTotalLabel: reconciliationLabel,
    matchRateLabel: reconciliationLabel,
    statementsTitle: reconciliationLabel,
    importTitle: reconciliationLabel,
    detailTitle: reconciliationLabel,
    sourceTypeLabel: reconciliationLabel,
    sourceLabel: reconciliationLabel,
    sourceReferenceLabel: reconciliationLabel,
    accountNameLabel: reconciliationLabel,
    accountLastFourLabel: reconciliationLabel,
    periodStartLabel: reconciliationLabel,
    periodEndLabel: reconciliationLabel,
    openingBalanceLabel: reconciliationLabel,
    closingBalanceLabel: reconciliationLabel,
    statusLabel: reconciliationLabel,
    allStatusesLabel: reconciliationLabel,
    transactionDateLabel: reconciliationLabel,
    rowNumberLabel: reconciliationLabel,
    directionLabel: reconciliationLabel,
    kindLabel: reconciliationLabel,
    descriptionLabel: reconciliationLabel,
    counterpartyLabel: reconciliationLabel,
    employeeLabel: reconciliationLabel,
    externalReferenceLabel: reconciliationLabel,
    amountLabel: reconciliationLabel,
    targetLabel: reconciliationLabel,
    targetIdLabel: reconciliationLabel,
    candidateLabel: reconciliationLabel,
    matchConfidenceLabel: reconciliationLabel,
    reasonLabel: reconciliationLabel,
    importAction: reconciliationLabel,
    addRowAction: reconciliationLabel,
    removeRowAction: reconciliationLabel,
    openAction: reconciliationLabel,
    acceptCandidateAction: reconciliationLabel,
    manualMatchAction: reconciliationLabel,
    noReceiptAction: reconciliationLabel,
    ignoreAction: reconciliationLabel,
    reopenAction: reconciliationLabel,
    startReviewAction: reconciliationLabel,
    closeStatementAction: reconciliationLabel,
    cancelAction: reconciliationLabel,
    loadingLabel: reconciliationLabel,
    emptyLabel: z.string().trim().min(1).max(260),
    loadErrorLabel: z.string().trim().min(1).max(260),
    mutationErrorLabel: z.string().trim().min(1).max(260),
    normalizedImportHelp: z.string().trim().min(1).max(500),
    adapterImportTitle: reconciliationLabel,
    adapterImportHelp: z.string().trim().min(1).max(500),
    adapterFormatLabel: reconciliationLabel,
    adapterSourceTextLabel: reconciliationLabel,
    adapterSourceFileNameLabel: reconciliationLabel,
    adapterNormalizeAction: reconciliationLabel,
    adapterImportAction: reconciliationLabel,
    adapterAcceptedRowsLabel: reconciliationLabel,
    adapterRejectedRowsLabel: reconciliationLabel,
    adapterWarningsLabel: reconciliationLabel,
    createExpenseTitle: reconciliationLabel,
    createExpenseAction: reconciliationLabel,
    createExpenseDescriptionPrefix: reconciliationLabel,
    costCenterCodeLabel: reconciliationLabel,
    accountCategoryCodeLabel: reconciliationLabel,
    requestReceiptTitle: reconciliationLabel,
    requestReceiptAction: reconciliationLabel,
    recipientLabel: reconciliationLabel,
    requestMessageLabel: reconciliationLabel,
    receiptRequestHistoryLabel: reconciliationLabel,
    receiptRequestPreparedLabel: reconciliationLabel,
    adapterFormats: z.record(z.enum(['chase-ink-text-v1', 'stripe-report-csv-v1']), reconciliationLabel),
    adapterEvidenceMessages: z.record(financeReconciliationAdapterEvidenceCodeSchema, reconciliationLabel),
    statementStatuses: z.record(z.enum(['ready', 'in-review', 'reconciled', 'closed', 'failed']), reconciliationLabel),
    matchStatuses: z.record(z.enum(['unmatched', 'suggested', 'auto-matched', 'manual-matched', 'no-receipt', 'ignored']), reconciliationLabel),
    sourceTypes: z.record(z.enum(['bank-statement', 'card-statement', 'processor-report', 'manual']), reconciliationLabel),
    directions: z.record(z.enum(['debit', 'credit']), reconciliationLabel),
    transactionKinds: z.record(z.enum(['purchase', 'payment', 'deposit', 'refund', 'fee', 'transfer', 'other']), reconciliationLabel),
    targetTypes: z.record(z.enum(['expense', 'payment', 'bill-occurrence']), reconciliationLabel),
});
const financeReconciliationCopyVersionTwoAdditions = {
    adapterImportTitle: 'Normalize a statement source',
    adapterImportHelp: 'Paste decoded Chase statement text or a Stripe report CSV. The source is normalized in memory and is not retained by CREST.',
    adapterFormatLabel: 'Source format',
    adapterSourceTextLabel: 'Decoded statement text or CSV',
    adapterSourceFileNameLabel: 'Source file name',
    adapterNormalizeAction: 'Normalize source',
    adapterImportAction: 'Import accepted rows',
    adapterAcceptedRowsLabel: 'Accepted rows',
    adapterRejectedRowsLabel: 'Rejected rows',
    adapterWarningsLabel: 'Normalization notes',
    createExpenseTitle: 'Create expense from transaction',
    createExpenseAction: 'Create and match expense',
    createExpenseDescriptionPrefix: 'Created from reconciliation transaction',
    costCenterCodeLabel: 'Cost center code',
    accountCategoryCodeLabel: 'Account category code',
    requestReceiptTitle: 'Prepare receipt request',
    requestReceiptAction: 'Prepare request',
    recipientLabel: 'Recipient',
    requestMessageLabel: 'Request message',
    receiptRequestHistoryLabel: 'Prepared receipt requests',
    receiptRequestPreparedLabel: 'Prepared',
    adapterFormats: {
        'chase-ink-text-v1': 'Chase Ink extracted text',
        'stripe-report-csv-v1': 'Stripe reconciliation report CSV',
    },
    adapterEvidenceMessages: {
        'invalid-date': 'The source row contains an invalid date.',
        'invalid-amount': 'The source row contains an invalid amount.',
        'missing-date': 'The source row does not contain a supported date.',
        'missing-amount': 'The source row does not contain a supported amount.',
        'missing-description': 'The source row does not contain a supported description.',
        'unparsed-transaction-row': 'The source row resembles a transaction but does not match the selected format.',
        'duplicate-header': 'Duplicate CSV headers use the first matching value.',
        'period-derived-from-rows': 'The statement period was derived from accepted transaction dates.',
        'source-row-limit-reached': 'The configured statement row limit was reached.',
    },
};
export const financeReconciliationCopySetting = definePlatformSetting({
    definition: {
        key: 'finance.copy.reconciliation',
        moduleId: 'finance',
        title: 'Reconciliation interface copy',
        helpText: 'Controls tenant-editable statement, transaction, candidate, and decision labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            navigationLabel: 'Reconciliation', title: 'Financial reconciliation',
            description: 'Import normalized statement rows, match Finance records, and retain every decision.',
            dashboardTitle: 'Reconciliation summary', statementCountLabel: 'Statements',
            transactionCountLabel: 'Transactions', matchedCountLabel: 'Matched',
            unresolvedCountLabel: 'Unresolved', noReceiptCountLabel: 'No receipt',
            ignoredCountLabel: 'Ignored', debitTotalLabel: 'Money out',
            creditTotalLabel: 'Money in', matchRateLabel: 'Match rate', statementsTitle: 'Statements',
            importTitle: 'Add normalized statement', detailTitle: 'Statement review',
            sourceTypeLabel: 'Source type', sourceLabel: 'Source name',
            sourceReferenceLabel: 'Statement reference', accountNameLabel: 'Account name',
            accountLastFourLabel: 'Account last four', periodStartLabel: 'Period start',
            periodEndLabel: 'Period end', openingBalanceLabel: 'Opening balance',
            closingBalanceLabel: 'Closing balance', statusLabel: 'Status', allStatusesLabel: 'All statuses',
            transactionDateLabel: 'Posted date', rowNumberLabel: 'Row', directionLabel: 'Direction', kindLabel: 'Kind',
            descriptionLabel: 'Description', counterpartyLabel: 'Counterparty',
            employeeLabel: 'Employee', externalReferenceLabel: 'External reference', amountLabel: 'Amount',
            targetLabel: 'Matched record', targetIdLabel: 'Record ID', candidateLabel: 'Candidate',
            matchConfidenceLabel: 'Match confidence', reasonLabel: 'Reason',
            importAction: 'Import normalized rows', addRowAction: 'Add row', removeRowAction: 'Remove row',
            openAction: 'Review statement', acceptCandidateAction: 'Accept candidate',
            manualMatchAction: 'Match by ID', noReceiptAction: 'No receipt', ignoreAction: 'Ignore',
            reopenAction: 'Reopen', startReviewAction: 'Start review',
            closeStatementAction: 'Close statement', cancelAction: 'Cancel',
            loadingLabel: 'Loading reconciliation…', emptyLabel: 'No statements match this view.',
            loadErrorLabel: 'Reconciliation information is unavailable.',
            mutationErrorLabel: 'The reconciliation change was not saved. Refresh and try again.',
            normalizedImportHelp: 'Enter normalized rows here; provider-specific PDF, bank, Stripe, and QuickBooks adapters will use the same contract later.',
            ...financeReconciliationCopyVersionTwoAdditions,
            statementStatuses: { ready: 'Ready', 'in-review': 'In review', reconciled: 'Reconciled', closed: 'Closed', failed: 'Failed' },
            matchStatuses: { unmatched: 'Unmatched', suggested: 'Suggested', 'auto-matched': 'Auto matched', 'manual-matched': 'Manually matched', 'no-receipt': 'No receipt', ignored: 'Ignored' },
            sourceTypes: { 'bank-statement': 'Bank statement', 'card-statement': 'Card statement', 'processor-report': 'Processor report', manual: 'Manual' },
            directions: { debit: 'Money out', credit: 'Money in' },
            transactionKinds: { purchase: 'Purchase', payment: 'Payment', deposit: 'Deposit', refund: 'Refund', fee: 'Fee', transfer: 'Transfer', other: 'Other' },
            targetTypes: { expense: 'Expense', payment: 'Payment', 'bill-occurrence': 'Bill occurrence' },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: financeReconciliationCopyValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => ({
                ...financeReconciliationCopyVersionTwoAdditions,
                ...(typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}),
            }),
        }],
});
export const financePaymentReconciliationConfigurationValueSchema = z.strictObject({
    currencyCode: z.literal('USD'),
    defaultProviderKey: z.string().regex(/^[a-z][a-z0-9-]{0,49}$/),
    processorEntryRecordingEnabled: z.boolean(),
    payoutTrackingEnabled: z.boolean(),
    disputeTrackingEnabled: z.boolean(),
    refundRecordingEnabled: z.boolean(),
    maximumProcessorEntriesPerPayment: z.number().int().min(1).max(500),
    maximumDisputesPerPayment: z.number().int().min(1).max(500),
    maximumRefundsPerPayment: z.number().int().min(1).max(500),
});
export const financePaymentReconciliationConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'finance.payments.configuration',
        moduleId: 'finance',
        title: 'Payment reconciliation configuration',
        helpText: 'Controls protected local recording limits for payment settlement, payout, dispute, and refund evidence.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            currencyCode: 'USD',
            defaultProviderKey: 'stripe',
            processorEntryRecordingEnabled: true,
            payoutTrackingEnabled: true,
            disputeTrackingEnabled: true,
            refundRecordingEnabled: true,
            maximumProcessorEntriesPerPayment: 100,
            maximumDisputesPerPayment: 100,
            maximumRefundsPerPayment: 100,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'protected',
    },
    valueSchema: financePaymentReconciliationConfigurationValueSchema,
});
export const financePaymentReconciliationDisplayValueSchema = z.strictObject({
    cardSize: z.enum(['compact', 'standard', 'wide']),
    density: z.enum(['comfortable', 'compact']),
    defaultStatus: z.enum([
        'all', 'pending', 'cleared', 'failed', 'refunded', 'disputed', 'reversed',
    ]),
    showSummary: z.boolean(),
    showProcessorFees: z.boolean(),
    showProviderReferences: z.boolean(),
    showPayouts: z.boolean(),
    showDisputes: z.boolean(),
    showRefunds: z.boolean(),
    showBankClearing: z.boolean(),
    listPageSize: z.number().int().min(10).max(250),
});
export const financePaymentReconciliationDisplaySetting = definePlatformSetting({
    definition: {
        key: 'finance.payments.display',
        moduleId: 'finance',
        title: 'Payments display',
        helpText: 'Controls tenant and user payment card sizing, status filters, and settlement evidence visibility.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            cardSize: 'standard', density: 'comfortable', defaultStatus: 'all',
            showSummary: true, showProcessorFees: true, showProviderReferences: true,
            showPayouts: true, showDisputes: true, showRefunds: true,
            showBankClearing: true, listPageSize: 50,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: financePaymentReconciliationDisplayValueSchema,
});
const paymentReconciliationLabel = z.string().trim().min(1).max(160);
export const financePaymentReconciliationCopyValueSchema = z.strictObject({
    navigationLabel: paymentReconciliationLabel,
    title: paymentReconciliationLabel,
    description: z.string().trim().min(1).max(360),
    summaryTitle: paymentReconciliationLabel,
    totalReceivedLabel: paymentReconciliationLabel,
    netReceivedLabel: paymentReconciliationLabel,
    clearedLabel: paymentReconciliationLabel,
    pendingLabel: paymentReconciliationLabel,
    refundedLabel: paymentReconciliationLabel,
    paymentCountLabel: paymentReconciliationLabel,
    invoiceLabel: paymentReconciliationLabel,
    methodLabel: paymentReconciliationLabel,
    paymentDateLabel: paymentReconciliationLabel,
    amountLabel: paymentReconciliationLabel,
    statusLabel: paymentReconciliationLabel,
    allStatusesLabel: paymentReconciliationLabel,
    openAction: paymentReconciliationLabel,
    closeAction: paymentReconciliationLabel,
    loadingLabel: paymentReconciliationLabel,
    emptyLabel: z.string().trim().min(1).max(260),
    loadErrorLabel: z.string().trim().min(1).max(260),
    mutationErrorLabel: z.string().trim().min(1).max(260),
    detailTitle: paymentReconciliationLabel,
    processorEntriesTitle: paymentReconciliationLabel,
    recordProcessorEntryTitle: paymentReconciliationLabel,
    recordProcessorEntryAction: paymentReconciliationLabel,
    providerLabel: paymentReconciliationLabel,
    externalReferenceLabel: paymentReconciliationLabel,
    processorKindLabel: paymentReconciliationLabel,
    occurredAtLabel: paymentReconciliationLabel,
    grossLabel: paymentReconciliationLabel,
    feeLabel: paymentReconciliationLabel,
    netLabel: paymentReconciliationLabel,
    processorStatusLabel: paymentReconciliationLabel,
    paymentIntentLabel: paymentReconciliationLabel,
    chargeLabel: paymentReconciliationLabel,
    balanceTransactionLabel: paymentReconciliationLabel,
    payoutReferenceLabel: paymentReconciliationLabel,
    payoutsTitle: paymentReconciliationLabel,
    recordPayoutTitle: paymentReconciliationLabel,
    recordPayoutAction: paymentReconciliationLabel,
    payoutStatusAction: paymentReconciliationLabel,
    payoutAmountLabel: paymentReconciliationLabel,
    arrivalDateLabel: paymentReconciliationLabel,
    destinationLabel: paymentReconciliationLabel,
    allocatePayoutAction: paymentReconciliationLabel,
    reverseAllocationAction: paymentReconciliationLabel,
    disputesTitle: paymentReconciliationLabel,
    recordDisputeTitle: paymentReconciliationLabel,
    recordDisputeAction: paymentReconciliationLabel,
    disputeStatusAction: paymentReconciliationLabel,
    evidenceDueLabel: paymentReconciliationLabel,
    refundsTitle: paymentReconciliationLabel,
    recordRefundTitle: paymentReconciliationLabel,
    recordRefundAction: paymentReconciliationLabel,
    refundedOnLabel: paymentReconciliationLabel,
    bankClearingsTitle: paymentReconciliationLabel,
    reasonLabel: paymentReconciliationLabel,
    clearingStatuses: z.record(z.enum(['pending', 'cleared', 'failed', 'refunded', 'disputed', 'reversed']), paymentReconciliationLabel),
    paymentMethods: z.record(z.enum(['credit-card', 'ach', 'check', 'cash', 'avidxchange', 'other']), paymentReconciliationLabel),
    processorKinds: z.record(z.enum(['charge', 'refund', 'adjustment']), paymentReconciliationLabel),
    payoutStatuses: z.record(z.enum(['pending', 'paid', 'failed', 'canceled']), paymentReconciliationLabel),
    disputeStatuses: z.record(z.enum(['open', 'under-review', 'won', 'lost', 'withdrawn']), paymentReconciliationLabel),
});
export const financePaymentReconciliationCopySetting = definePlatformSetting({
    definition: {
        key: 'finance.copy.payments',
        moduleId: 'finance',
        title: 'Payments interface copy',
        helpText: 'Controls tenant-editable payment settlement, payout, dispute, refund, and clearing labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Payments', title: 'Payments',
            description: 'Review collected cash, processor settlement, payout, dispute, refund, and bank-clearing evidence.',
            summaryTitle: 'Payment summary', totalReceivedLabel: 'Total received',
            netReceivedLabel: 'Net received', clearedLabel: 'Cleared', pendingLabel: 'Pending',
            refundedLabel: 'Refunded', paymentCountLabel: 'Payments', invoiceLabel: 'Invoice',
            methodLabel: 'Method', paymentDateLabel: 'Payment date', amountLabel: 'Amount',
            statusLabel: 'Clearing status', allStatusesLabel: 'All statuses', openAction: 'Review payment',
            closeAction: 'Close', loadingLabel: 'Loading payments…', emptyLabel: 'No payments match this view.',
            loadErrorLabel: 'Payment reconciliation is unavailable.',
            mutationErrorLabel: 'The payment reconciliation change was not saved. Refresh and try again.',
            detailTitle: 'Payment reconciliation detail', processorEntriesTitle: 'Processor entries',
            recordProcessorEntryTitle: 'Record processor entry',
            recordProcessorEntryAction: 'Record processor entry', providerLabel: 'Provider key',
            externalReferenceLabel: 'External reference', processorKindLabel: 'Entry kind',
            occurredAtLabel: 'Occurred at', grossLabel: 'Gross', feeLabel: 'Signed fee', netLabel: 'Net',
            processorStatusLabel: 'Processor status', paymentIntentLabel: 'Payment intent reference',
            chargeLabel: 'Charge reference', balanceTransactionLabel: 'Balance transaction reference',
            payoutReferenceLabel: 'Payout reference', payoutsTitle: 'Payouts',
            recordPayoutTitle: 'Record payout', recordPayoutAction: 'Record payout',
            payoutStatusAction: 'Update payout status', payoutAmountLabel: 'Payout amount',
            arrivalDateLabel: 'Arrival date', destinationLabel: 'Destination reference',
            allocatePayoutAction: 'Allocate payout', reverseAllocationAction: 'Reverse allocation',
            disputesTitle: 'Disputes', recordDisputeTitle: 'Record dispute',
            recordDisputeAction: 'Record dispute', disputeStatusAction: 'Update dispute status',
            evidenceDueLabel: 'Evidence due', refundsTitle: 'Refunds',
            recordRefundTitle: 'Record refund', recordRefundAction: 'Record refund',
            refundedOnLabel: 'Refunded on', bankClearingsTitle: 'Bank clearing evidence', reasonLabel: 'Reason',
            clearingStatuses: {
                pending: 'Pending', cleared: 'Cleared', failed: 'Failed', refunded: 'Refunded',
                disputed: 'Disputed', reversed: 'Reversed',
            },
            paymentMethods: {
                'credit-card': 'Card', ach: 'Bank ACH', check: 'Check', cash: 'Cash',
                avidxchange: 'AvidXchange', other: 'Other',
            },
            processorKinds: { charge: 'Charge', refund: 'Refund', adjustment: 'Adjustment' },
            payoutStatuses: { pending: 'Pending', paid: 'Paid', failed: 'Failed', canceled: 'Canceled' },
            disputeStatuses: {
                open: 'Open', 'under-review': 'Under review', won: 'Won', lost: 'Lost', withdrawn: 'Withdrawn',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: financePaymentReconciliationCopyValueSchema,
});
const financeProjectProfitabilityCostingModeSchema = z.enum(['required', 'not-applicable']);
export const financeProjectProfitabilityConfigurationValueSchema = z.strictObject({
    currencyCode: z.literal('USD'),
    maximumProjects: z.number().int().min(10).max(500),
    maximumHistoryYears: z.number().int().min(1).max(30),
    maximumAcceptedEstimates: z.number().int().min(100).max(10_000),
    laborCostingMode: financeProjectProfitabilityCostingModeSchema,
    equipmentCostingMode: financeProjectProfitabilityCostingModeSchema,
    procurementCostingMode: financeProjectProfitabilityCostingModeSchema,
    commissionCostingMode: financeProjectProfitabilityCostingModeSchema,
});
export const financeProjectProfitabilityConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'finance.profitability.configuration',
        moduleId: 'finance',
        title: 'Project profitability configuration',
        helpText: 'Controls bounded history and which owner-module cost sources are required before a Project profit is complete.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            currencyCode: 'USD',
            maximumProjects: 250,
            maximumHistoryYears: 10,
            maximumAcceptedEstimates: 2_500,
            laborCostingMode: 'required',
            equipmentCostingMode: 'required',
            procurementCostingMode: 'required',
            commissionCostingMode: 'required',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'protected',
    },
    valueSchema: financeProjectProfitabilityConfigurationValueSchema,
});
export const financeProjectProfitabilityDisplayValueSchema = z.strictObject({
    cardSize: z.enum(['compact', 'standard', 'wide']),
    density: z.enum(['comfortable', 'compact']),
    listPageSize: z.number().int().min(10).max(100),
    defaultResult: z.enum(['all', 'profitable', 'loss', 'incomplete']),
    defaultSort: z.enum(['profit-desc', 'revenue-desc', 'created-desc']),
    showSummary: z.boolean(),
    showContractValue: z.boolean(),
    showCollected: z.boolean(),
    showKnownCosts: z.boolean(),
    showProviderStates: z.boolean(),
    showOverhead: z.boolean(),
});
export const financeProjectProfitabilityDisplaySetting = definePlatformSetting({
    definition: {
        key: 'finance.profitability.display',
        moduleId: 'finance',
        title: 'Project profitability display',
        helpText: 'Controls tenant and user card sizing, density, default filters, and cost evidence visibility.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            cardSize: 'standard',
            density: 'comfortable',
            listPageSize: 30,
            defaultResult: 'all',
            defaultSort: 'profit-desc',
            showSummary: true,
            showContractValue: true,
            showCollected: true,
            showKnownCosts: true,
            showProviderStates: true,
            showOverhead: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: financeProjectProfitabilityDisplayValueSchema,
});
const financeProjectProfitabilityLabel = z.string().trim().min(1).max(180);
export const financeProjectProfitabilityCopyValueSchema = z.strictObject({
    navigationLabel: financeProjectProfitabilityLabel,
    title: financeProjectProfitabilityLabel,
    description: z.string().trim().min(1).max(360),
    summaryTitle: financeProjectProfitabilityLabel,
    projectCountLabel: financeProjectProfitabilityLabel,
    invoicedLabel: financeProjectProfitabilityLabel,
    collectedLabel: financeProjectProfitabilityLabel,
    knownCostLabel: financeProjectProfitabilityLabel,
    completeProfitLabel: financeProjectProfitabilityLabel,
    marginLabel: financeProjectProfitabilityLabel,
    contractValueLabel: financeProjectProfitabilityLabel,
    laborLabel: financeProjectProfitabilityLabel,
    equipmentLabel: financeProjectProfitabilityLabel,
    materialsLabel: financeProjectProfitabilityLabel,
    subcontractorsLabel: financeProjectProfitabilityLabel,
    directExpensesLabel: financeProjectProfitabilityLabel,
    commissionsLabel: financeProjectProfitabilityLabel,
    overheadLabel: financeProjectProfitabilityLabel,
    knownContributionLabel: financeProjectProfitabilityLabel,
    incompleteLabel: financeProjectProfitabilityLabel,
    completeLabel: financeProjectProfitabilityLabel,
    searchLabel: financeProjectProfitabilityLabel,
    projectStatusLabel: financeProjectProfitabilityLabel,
    allProjectStatusesLabel: financeProjectProfitabilityLabel,
    allResultsLabel: financeProjectProfitabilityLabel,
    profitableLabel: financeProjectProfitabilityLabel,
    lossLabel: financeProjectProfitabilityLabel,
    incompleteFilterLabel: financeProjectProfitabilityLabel,
    loadingLabel: financeProjectProfitabilityLabel,
    emptyLabel: z.string().trim().min(1).max(260),
    loadErrorLabel: z.string().trim().min(1).max(260),
    projectLimitLabel: z.string().trim().min(1).max(260),
    previousAction: financeProjectProfitabilityLabel,
    nextAction: financeProjectProfitabilityLabel,
    noAcceptedEstimateLabel: z.string().trim().min(1).max(260),
    providerStatesTitle: financeProjectProfitabilityLabel,
    unavailableReasonLabels: z.record(financeProjectProfitabilityUnavailableReasonSchema, financeProjectProfitabilityLabel),
});
export const financeProjectProfitabilityCopySetting = definePlatformSetting({
    definition: {
        key: 'finance.copy.profitability',
        moduleId: 'finance',
        title: 'Project profitability interface copy',
        helpText: 'Controls tenant-editable Project profitability, cost, margin, and source-state labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Profitability',
            title: 'Project profitability',
            description: 'Review exact Finance revenue and known Project costs without treating missing owner facts as zero.',
            summaryTitle: 'Profitability summary',
            projectCountLabel: 'Projects',
            invoicedLabel: 'Invoiced revenue',
            collectedLabel: 'Collected revenue',
            knownCostLabel: 'Known costs',
            completeProfitLabel: 'Complete profit',
            marginLabel: 'Margin',
            contractValueLabel: 'Accepted contract value',
            laborLabel: 'Labor',
            equipmentLabel: 'Equipment',
            materialsLabel: 'Materials',
            subcontractorsLabel: 'Subcontractors',
            directExpensesLabel: 'Approved direct expenses',
            commissionsLabel: 'Referral commissions',
            overheadLabel: 'Allocated overhead',
            knownContributionLabel: 'Revenue less known costs',
            incompleteLabel: 'Incomplete cost evidence',
            completeLabel: 'Complete',
            searchLabel: 'Search Projects',
            projectStatusLabel: 'Project status',
            allProjectStatusesLabel: 'All Project statuses',
            allResultsLabel: 'All results',
            profitableLabel: 'Profitable',
            lossLabel: 'Loss',
            incompleteFilterLabel: 'Incomplete',
            loadingLabel: 'Loading Project profitability…',
            emptyLabel: 'No Projects match this profitability view.',
            loadErrorLabel: 'Project profitability is unavailable.',
            projectLimitLabel: 'This view reached the protected Project limit. Narrow the tenant configuration or review the next bounded batch.',
            previousAction: 'Previous',
            nextAction: 'Next',
            noAcceptedEstimateLabel: 'No accepted estimate is retained for this Project.',
            providerStatesTitle: 'Cost evidence',
            unavailableReasonLabels: {
                'not-installed': 'The owner module is not installed.',
                'not-enabled': 'The owner module is not enabled for this tenant.',
                'permission-denied': 'You cannot read this owner cost source.',
                'provider-invalid': 'The owner module returned invalid cost evidence.',
                'provider-failed': 'The owner module could not return cost evidence.',
                'history-truncated': 'The retained history does not cover the full Project.',
                'basis-incomplete': 'Required cost basis or payroll snapshots are incomplete.',
                'currency-mismatch': 'The owner cost currency does not match Finance.',
                'project-unavailable': 'The owner module cannot resolve this Project.',
                'cost-basis-incomplete': 'The owner module has records without complete internal cost.',
                'source-limit-exceeded': 'The protected source limit was reached before evidence completed.',
                'not-implemented': 'This required cost source is not implemented yet.',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: financeProjectProfitabilityCopyValueSchema,
});
export const financeReferralCommissionConfigurationValueSchema = z.strictObject({
    currencyCode: z.literal('USD'),
    maximumActiveRules: z.number().int().min(1).max(250),
    maximumSplitsPerRule: z.number().int().min(1).max(12),
    maximumHoldDays: z.number().int().min(0).max(365),
    autoExpenseEnabled: z.boolean(),
    requireReasonForEveryTransition: z.literal(true),
});
export const financeReferralCommissionConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'finance.commissions.configuration', moduleId: 'finance',
        title: 'Referral commission configuration',
        helpText: 'Controls protected tenant rule, split, hold, and Finance expense limits.',
        configurationDomain: 'module', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            currencyCode: 'USD', maximumActiveRules: 250, maximumSplitsPerRule: 12,
            maximumHoldDays: 365, autoExpenseEnabled: true,
            requireReasonForEveryTransition: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'protected',
    },
    valueSchema: financeReferralCommissionConfigurationValueSchema,
});
export const financeReferralCommissionDisplayValueSchema = z.strictObject({
    cardSize: z.enum(['compact', 'standard', 'wide']),
    density: z.enum(['comfortable', 'compact']),
    defaultView: z.enum(['payouts', 'rules']),
    defaultStatus: z.enum([
        'all', 'estimated', 'accrued', 'pending-payment', 'ready-for-approval',
        'approved', 'paid', 'locked', 'void', 'clawback-pending',
    ]),
    listPageSize: z.number().int().min(10).max(250),
    showRuleBasis: z.boolean(),
    showAttributionIds: z.boolean(),
});
export const financeReferralCommissionDisplaySetting = definePlatformSetting({
    definition: {
        key: 'finance.commissions.display', moduleId: 'finance',
        title: 'Referral commission display',
        helpText: 'Controls tenant and user commission card sizing, density, defaults, and detail visibility.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            cardSize: 'standard', density: 'comfortable', defaultView: 'payouts',
            defaultStatus: 'all', listPageSize: 50, showRuleBasis: true,
            showAttributionIds: false,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: true, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: financeReferralCommissionDisplayValueSchema,
});
const financeReferralCommissionLabel = z.string().trim().min(1).max(180);
export const financeReferralCommissionCopyValueSchema = z.strictObject({
    navigationLabel: financeReferralCommissionLabel,
    title: financeReferralCommissionLabel,
    description: z.string().trim().min(1).max(360),
    payoutsTabLabel: financeReferralCommissionLabel,
    rulesTabLabel: financeReferralCommissionLabel,
    statusLabel: financeReferralCommissionLabel,
    allStatusesLabel: financeReferralCommissionLabel,
    projectLabel: financeReferralCommissionLabel,
    amountLabel: financeReferralCommissionLabel,
    basisLabel: financeReferralCommissionLabel,
    ruleLabel: financeReferralCommissionLabel,
    nameLabel: financeReferralCommissionLabel,
    reasonLabel: financeReferralCommissionLabel,
    scopeLabel: financeReferralCommissionLabel,
    scopeIdLabel: financeReferralCommissionLabel,
    payoutTypeLabel: financeReferralCommissionLabel,
    percentLabel: financeReferralCommissionLabel,
    flatAmountLabel: financeReferralCommissionLabel,
    estimatedProfitPercentLabel: financeReferralCommissionLabel,
    holdDaysLabel: financeReferralCommissionLabel,
    holdUntilLabel: financeReferralCommissionLabel,
    creditedToLabel: financeReferralCommissionLabel,
    managerApprovalLabel: financeReferralCommissionLabel,
    autoExpenseLabel: financeReferralCommissionLabel,
    forceRecalculationLabel: financeReferralCommissionLabel,
    activeLabel: financeReferralCommissionLabel,
    inactiveLabel: financeReferralCommissionLabel,
    revisionLabel: financeReferralCommissionLabel,
    createRuleAction: financeReferralCommissionLabel,
    calculateAction: financeReferralCommissionLabel,
    saveRuleAction: financeReferralCommissionLabel,
    loadingLabel: financeReferralCommissionLabel,
    emptyPayoutsLabel: z.string().trim().min(1).max(260),
    emptyRulesLabel: z.string().trim().min(1).max(260),
    loadErrorLabel: z.string().trim().min(1).max(260),
    mutationErrorLabel: z.string().trim().min(1).max(260),
    noRuleLabel: z.string().trim().min(1).max(260),
    notAttributedLabel: z.string().trim().min(1).max(260),
    ambiguousAttributionLabel: z.string().trim().min(1).max(260),
    statuses: z.record(z.enum([
        'estimated', 'accrued', 'pending-payment', 'ready-for-approval',
        'approved', 'paid', 'locked', 'void', 'clawback-pending',
    ]), financeReferralCommissionLabel),
    scopes: z.record(z.enum(['tenant-default', 'referral-source', 'membership']), financeReferralCommissionLabel),
    bases: z.record(z.enum(['revenue', 'gross-margin', 'net-profit', 'estimated-net-profit']), financeReferralCommissionLabel),
    payoutTypes: z.record(z.enum(['percent', 'flat', 'none']), financeReferralCommissionLabel),
    transitions: z.record(z.enum([
        'accrue', 'queue-payment', 'submit-approval', 'approve',
        'mark-paid', 'lock', 'void', 'request-clawback',
    ]), financeReferralCommissionLabel),
});
export const financeReferralCommissionCopySetting = definePlatformSetting({
    definition: {
        key: 'finance.copy.commissions', moduleId: 'finance',
        title: 'Referral commission interface copy',
        helpText: 'Controls tenant-editable commission, payout, rule, and attribution labels.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Commissions', title: 'Referral commissions',
            description: 'Configure versioned referral rules and review retained exact-cent payouts.',
            payoutsTabLabel: 'Payouts', rulesTabLabel: 'Rules', statusLabel: 'Status',
            allStatusesLabel: 'All',
            projectLabel: 'Project', amountLabel: 'Payout', basisLabel: 'Calculation basis',
            ruleLabel: 'Rule', nameLabel: 'Name', reasonLabel: 'Reason', scopeLabel: 'Scope',
            scopeIdLabel: 'Scope ID', payoutTypeLabel: 'Payout type', percentLabel: 'Percent',
            flatAmountLabel: 'Flat amount', estimatedProfitPercentLabel: 'Estimated profit percent',
            holdDaysLabel: 'Hold days', holdUntilLabel: 'Hold until',
            creditedToLabel: 'Credited membership', managerApprovalLabel: 'Manager approval',
            autoExpenseLabel: 'Prepare Finance expense when paid',
            forceRecalculationLabel: 'Recalculate retained estimate',
            activeLabel: 'Active', inactiveLabel: 'Inactive', revisionLabel: 'Revision',
            createRuleAction: 'Create rule', calculateAction: 'Calculate commission',
            saveRuleAction: 'Save rule', loadingLabel: 'Loading referral commissions…',
            emptyPayoutsLabel: 'No referral commission payouts match this view.',
            emptyRulesLabel: 'No commission rule is configured. No hidden percentage is applied.',
            loadErrorLabel: 'Referral commissions are unavailable.',
            mutationErrorLabel: 'The commission change was not saved. Refresh and try again.',
            noRuleLabel: 'No configured rule matched this attribution.',
            notAttributedLabel: 'Relationships has no referral attribution for this Project.',
            ambiguousAttributionLabel: 'Relationships returned more than one attribution; resolve it before calculating.',
            statuses: {
                estimated: 'Estimated', accrued: 'Accrued', 'pending-payment': 'Pending payment',
                'ready-for-approval': 'Ready for approval', approved: 'Approved', paid: 'Paid',
                locked: 'Locked', void: 'Void', 'clawback-pending': 'Clawback pending',
            },
            scopes: {
                'tenant-default': 'Tenant default', 'referral-source': 'Referral source',
                membership: 'Membership',
            },
            bases: {
                revenue: 'Revenue', 'gross-margin': 'Gross margin',
                'net-profit': 'Net profit', 'estimated-net-profit': 'Estimated net profit',
            },
            payoutTypes: { percent: 'Percent', flat: 'Flat', none: 'None' },
            transitions: {
                accrue: 'Accrue', 'queue-payment': 'Queue payment',
                'submit-approval': 'Submit for approval', approve: 'Approve',
                'mark-paid': 'Mark paid', lock: 'Lock', void: 'Void',
                'request-clawback': 'Request clawback',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: financeReferralCommissionCopyValueSchema,
});
export const financePortalInvoicesDisplayValueSchema = z.strictObject({
    enabled: z.boolean(),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    density: z.enum(['compact', 'comfortable']),
    showLineItems: z.boolean(),
    showSubtotal: z.boolean(),
    showDiscount: z.boolean(),
    showTax: z.boolean(),
    showMemo: z.boolean(),
    showAmountPaid: z.boolean(),
});
export const financePortalInvoicesDisplaySetting = definePlatformSetting({
    definition: {
        key: 'finance.portal-invoices.display', moduleId: 'finance',
        title: 'Customer Portal invoice display',
        helpText: 'Controls the customer-safe issued-invoice panel, card size, density, and retained totals.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            enabled: true, cardSize: 'standard', density: 'comfortable',
            showLineItems: true, showSubtotal: true, showDiscount: true, showTax: true,
            showMemo: true, showAmountPaid: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: financePortalInvoicesDisplayValueSchema,
});
const financePortalInvoiceLabel = z.string().trim().min(1).max(120);
export const financePortalInvoicesCopyValueSchema = z.strictObject({
    title: financePortalInvoiceLabel,
    description: z.string().trim().min(1).max(300),
    loadingLabel: financePortalInvoiceLabel,
    emptyLabel: z.string().trim().min(1).max(240),
    unavailableLabel: z.string().trim().min(1).max(240),
    invoiceNumberLabel: financePortalInvoiceLabel,
    statusLabel: financePortalInvoiceLabel,
    invoiceDateLabel: financePortalInvoiceLabel,
    dueDateLabel: financePortalInvoiceLabel,
    memoLabel: financePortalInvoiceLabel,
    descriptionLabel: financePortalInvoiceLabel,
    quantityLabel: financePortalInvoiceLabel,
    unitPriceLabel: financePortalInvoiceLabel,
    subtotalLabel: financePortalInvoiceLabel,
    discountLabel: financePortalInvoiceLabel,
    taxLabel: financePortalInvoiceLabel,
    totalLabel: financePortalInvoiceLabel,
    amountPaidLabel: financePortalInvoiceLabel,
    amountDueLabel: financePortalInvoiceLabel,
    overdueLabel: financePortalInvoiceLabel,
    statuses: z.record(z.enum(['sent', 'partial', 'paid']), financePortalInvoiceLabel),
});
export const financePortalInvoicesCopySetting = definePlatformSetting({
    definition: {
        key: 'finance.copy.portal-invoices', moduleId: 'finance',
        title: 'Customer Portal invoice copy',
        helpText: 'Controls tenant-editable public invoice labels without changing retained money or status meaning.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            title: 'Project invoices',
            description: 'Review issued invoices and their current balance for this project.',
            loadingLabel: 'Loading invoices…',
            emptyLabel: 'No issued invoices are available for this project.',
            unavailableLabel: 'Project invoices are not currently available.',
            invoiceNumberLabel: 'Invoice', statusLabel: 'Status', invoiceDateLabel: 'Invoice date',
            dueDateLabel: 'Due date', memoLabel: 'Memo', descriptionLabel: 'Description',
            quantityLabel: 'Quantity', unitPriceLabel: 'Unit price', subtotalLabel: 'Subtotal',
            discountLabel: 'Discount', taxLabel: 'Tax', totalLabel: 'Total',
            amountPaidLabel: 'Paid', amountDueLabel: 'Amount due',
            overdueLabel: 'Overdue', statuses: { sent: 'Sent', partial: 'Partially paid', paid: 'Paid' },
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: financePortalInvoicesCopyValueSchema,
});
export const financeSettingRegistrations = [
    financeExpenseConfigurationSetting,
    financeExpenseDisplaySetting,
    financeExpenseCopySetting,
    financeInvoiceConfigurationSetting,
    financeInvoiceDisplaySetting,
    financeInvoiceCopySetting,
    financeInvoiceLifecycleCopySetting,
    financeInvoiceContentCopySetting,
    financeOverheadDisplaySetting,
    financeOverheadCopySetting,
    financeReconciliationConfigurationSetting,
    financeReconciliationDisplaySetting,
    financeReconciliationCopySetting,
    financePaymentReconciliationConfigurationSetting,
    financePaymentReconciliationDisplaySetting,
    financePaymentReconciliationCopySetting,
    financeProjectProfitabilityConfigurationSetting,
    financeProjectProfitabilityDisplaySetting,
    financeProjectProfitabilityCopySetting,
    financeReferralCommissionConfigurationSetting,
    financeReferralCommissionDisplaySetting,
    financeReferralCommissionCopySetting,
    financePortalInvoicesDisplaySetting,
    financePortalInvoicesCopySetting,
];
//# sourceMappingURL=finance-setting-definitions.js.map